/**
 * The bridge between a FieldDef and the design system: one control per
 * `ControlKind`, so no page ever branches on a document type to draw a field.
 */
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { lookupNarrowing, lookupOwners, narrowingFields, sanitizeRichText, type FieldDef } from '@project/contracts'
import { useApi } from '../app/api.js'
import { Button, Field, Input, Lookup, Select } from '../design/index.js'
import { asText } from './format.js'
import { QuickMasterDialog } from './QuickMaster.js'
import { MultiLookup } from './MultiLookup.js'

/**
 * Masters somebody adds mid-document (docs/02 §4c, docs/06 §Form).
 *
 * A site address or a supplier's contact is discovered while raising the order
 * that needs it. Sending the buyer to Masters, and back, and asking them to
 * remember where they were, is how a purchase order gets raised against the
 * wrong address instead.
 */
const CREATABLE: Readonly<Record<string, string>> = {
  address: 'address',
  contact: 'contact',
}

export interface ControlProps {
  readonly field: FieldDef
  readonly value: unknown
  readonly onChange: (value: unknown) => void
  readonly disabled?: boolean
  readonly invalid?: boolean
  /** Display text for an id the form already holds, e.g. the supplier's name. */
  readonly valueLabel?: string | undefined
  /**
   * The other answers on the same form, which a scoped lookup reads its owner
   * from — the supplier a "Supplier's contact" belongs to (BILL-008).
   */
  readonly scopeValues?: Readonly<Record<string, unknown>> | undefined
}

/** Money, quantity, rate and percentage inputs align right with tabular figures. */
const DECIMAL_CONTROLS = new Set(['number', 'money', 'qty', 'rate', 'percent'])

export const Control = ({ field, value, onChange, disabled, invalid, valueLabel, scopeValues }: ControlProps) => {
  if (field.unitPicker) {
    return (
      <UnitSelect
        value={asText(value)}
        onChange={onChange}
        {...(disabled === undefined ? {} : { disabled })}
        {...(invalid === undefined ? {} : { invalid })}
      />
    )
  }

  if (field.control === 'lookup') {
    return (
      <LookupControl
        field={field}
        value={value}
        onChange={onChange}
        {...(disabled === undefined ? {} : { disabled })}
        {...(invalid === undefined ? {} : { invalid })}
        {...(valueLabel === undefined ? {} : { valueLabel })}
        {...(scopeValues === undefined ? {} : { scopeValues })}
      />
    )
  }

  if (field.control === 'select') {
    const options = (field.options ?? []).map((o) => ({ value: o.value, label: o.label }))
    // A stored answer the list no longer offers (a currency typed by hand
    // before it became a pick) still shows, rather than reading as blank.
    const current = asText(value)
    if (current !== '' && !options.some((o) => o.value === current)) options.unshift({ value: current, label: current })
    return (
      <Select
        value={current}
        disabled={disabled}
        invalid={invalid}
        placeholder="Not chosen"
        options={options}
        onChange={onChange}
      />
    )
  }

  if (field.control === 'colour') {
    const current = asText(value)
    const picked = (field.options ?? []).find((o) => o.value === current)
    return (
      <div className="swatch-pick">
        <div role="radiogroup" aria-label={field.label} className="swatch-pick__row">
          {(field.options ?? []).map((o) => (
            <button key={o.value} type="button" role="radio" aria-checked={current === o.value} aria-label={o.label}
              title={o.label} disabled={disabled} className={`swatch-pick__swatch${current === o.value ? ' swatch-pick__swatch--on' : ''}`}
              style={{ background: o.value }} onClick={() => onChange(o.value)} />
          ))}
        </div>
        {current ? (
          <span className="chip" style={{ color: current, borderColor: current }}>
            {asText(scopeValues?.['name']) || picked?.label || current}
          </span>
        ) : null}
      </div>
    )
  }

  if (field.control === 'multiselect') {
    const chosen = asText(value).split(',').map((v) => v.trim()).filter(Boolean)
    const labelOf = (v: string): string => field.options?.find((o) => o.value === v)?.label ?? v
    const left = (field.options ?? []).filter((o) => !chosen.includes(o.value))
    return (
      <div className="stack" style={{ gap: 6 }}>
        <Select
          value=""
          disabled={disabled || left.length === 0}
          invalid={invalid}
          placeholder={left.length === 0 ? 'All chosen' : 'Add…'}
          options={left.map((o) => ({ value: o.value, label: o.label }))}
          onChange={(v) => { if (v) onChange([...chosen, v].join(',')) }}
        />
        {chosen.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {chosen.map((v) => (
              <button key={v} type="button" className="chip chip--removable" disabled={disabled}
                aria-label={`Remove ${labelOf(v)}`} onClick={() => onChange(chosen.filter((x) => x !== v).join(','))}>
                {labelOf(v)} ×
              </button>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  if (field.control === 'radio') {
    return (
      <div role="radiogroup" aria-label={field.label} className="stack" style={{ gap: 0 }}>
        {(field.options ?? []).map((o) => (
          <label key={o.value} className="row" style={{ minHeight: 44, cursor: disabled ? 'default' : 'pointer' }}>
            <input
              type="radio"
              name={field.key}
              value={o.value}
              checked={asText(value) === o.value}
              disabled={disabled}
              onChange={() => onChange(o.value)}
              style={{ width: 18, height: 18 }}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    )
  }

  if (field.control === 'checkbox') {
    return (
      <label className="row" style={{ minHeight: 44, cursor: disabled ? 'default' : 'pointer' }}>
        <input
          type="checkbox"
          checked={value === true || value === 'true'}
          disabled={disabled}
          onChange={(e) => onChange(e.currentTarget.checked)}
          style={{ width: 18, height: 18 }}
        />
        <span>{field.label}</span>
      </label>
    )
  }

  if (field.control === 'richtext') {
    return <RichTextControl field={field} value={value} onChange={onChange} {...(disabled === undefined ? {} : { disabled })} />
  }

  if (field.control === 'textarea') {
    return (
      <textarea
        className="line-input"
        rows={3}
        style={{ minHeight: 88, padding: 8, resize: 'vertical' }}
        value={asText(value)}
        disabled={disabled}
        aria-label={field.label}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    )
  }

  const numeric = DECIMAL_CONTROLS.has(field.control)
  return (
    <Input
      type={field.control === 'date' ? 'date' : 'text'}
      inputMode={numeric ? 'decimal' : undefined}
      align={numeric ? 'right' : 'left'}
      value={asText(value)}
      disabled={disabled}
      invalid={invalid}
      onChange={onChange}
    />
  )
}

/**
 * A unit, picked from the Units master and stored as its code (`unitPicker`,
 * HOME-014) — "Nos", as every item and line already holds it. A unit the
 * record carries that is not in the master (free text from before) stays
 * listed, so opening an old item never blanks its unit.
 */
const UnitSelect = ({ value, onChange, disabled, invalid }: {
  value: string; onChange: (value: unknown) => void; disabled?: boolean; invalid?: boolean
}) => {
  const api = useApi()
  const units = useQuery({
    queryKey: ['units', api.companyId],
    queryFn: () => api.masterList('unit', { pageSize: '200' }),
    staleTime: 5 * 60_000,
  })
  const options = (units.data?.rows ?? []).map((u) => {
    const code = asText(u['code'])
    const name = asText(u['name'])
    return { value: code, label: name && name !== code ? `${code} — ${name}` : code }
  }).filter((o) => o.value !== '')
  if (value !== '' && !options.some((o) => o.value === value)) options.unshift({ value, label: value })
  return (
    <Select value={value} disabled={disabled} invalid={invalid} placeholder="Not chosen" options={options} onChange={onChange} />
  )
}

export interface FormFieldProps extends ControlProps {
  readonly error?: string | undefined
}

/** Label above the control, the word "Required" printed, hint underneath. */
export const FormField = ({ error, ...props }: FormFieldProps) => {
  const { field } = props
  const full = field.width === 'full' || field.control === 'textarea' || field.control === 'richtext'
  return (
    <div className={full ? 'field--full' : undefined}>
      <Field label={field.label} required={field.required === true} hint={field.hint} error={error}>
        <Control {...props} disabled={props.disabled ?? field.readOnly === true} invalid={error !== undefined} />
      </Field>
    </div>
  )
}

/**
 * What a picked address or contact reads as, under the field (BILL-008).
 *
 * The reference product shows the whole card — lines, GSTIN, email, phone —
 * so the person raising an order can see it is going to the right place
 * without opening the master.
 */
const CARD_LINES: Readonly<Record<string, (x: Readonly<Record<string, string | null>>) => string[]>> = {
  address: (x) => [
    x['line1'] ?? '', x['line2'] ?? '',
    [x['city'], x['state'], x['postalCode']].filter(Boolean).join(', '),
    x['gstin'] ? `GSTIN ${x['gstin']}` : '',
  ].filter((l) => l.trim() !== ''),
  contact: (x) => [
    x['designation'] ?? '', x['email'] ?? '',
    [x['mobile'], x['landline']].filter(Boolean).join(' · '),
  ].filter((l) => l.trim() !== ''),
}

const LookupCard = ({ entity, value }: { entity: string; value: string }) => {
  const api = useApi()
  const card = useQuery({
    queryKey: ['lookupCard', entity, value],
    queryFn: async () => (await api.lookupById(entity, value)) ?? null,
    enabled: value !== '' && CARD_LINES[entity] !== undefined,
    staleTime: 60_000,
  })
  const lines = card.data?.extra ? CARD_LINES[entity]!(card.data.extra) : []
  if (lines.length === 0) return null
  return (
    <div className="lookup-card" aria-label="Selected details">
      {lines.map((line) => <div key={line}>{line}</div>)}
    </div>
  )
}

/**
 * A lookup, with `Add new …` beside it for the masters that are discovered
 * while a document is being raised.
 *
 * What is created is selected straight away — the point is not to have added a
 * record, it is to have filled in this field.
 *
 * A scoped lookup (`field.lookup.scope`) lists only its owner's records, opens
 * on them without typing, says "No entries" until its owner is chosen, and
 * moves to the new owner's default when the owner changes — a new supplier
 * brings its own billing address and contact with it (BILL-008).
 */
const LookupControl = ({ field, value, onChange, disabled, invalid, valueLabel, scopeValues }: ControlProps) => {
  const api = useApi()
  const entity = field.lookup?.entity ?? ''
  const creatable = CREATABLE[entity]
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState<string | undefined>(undefined)
  const current = asText(value)

  const owners = lookupOwners(field, scopeValues ?? {})
  const scoped = owners !== undefined
  const firstScope = field.lookup?.scope?.[0]
  const ownerName = firstScope?.field ? firstScope.field.replace(/Id$/, '').replace(/([A-Z])/g, ' $1').toLowerCase() : 'owner'

  /*
    Narrowed by earlier answers (BILL-002): a supplier's orders, a customer's
    projects, a project's open requests. Nothing is listed until what it
    depends on is chosen, and a pick made under another supplier is cleared
    when the supplier changes — it is not one of this supplier's orders.
  */
  const narrow = lookupNarrowing(field, scopeValues ?? {})
  const narrowKey = narrow === undefined ? undefined : narrow === null ? null : JSON.stringify(narrow)
  const dependsOn = narrowingFields(field)[0]
  const dependsOnName = dependsOn ? dependsOn.replace(/Id$/, '').replace(/([A-Z])/g, ' $1').toLowerCase() : 'earlier fields'
  const multiple = field.lookup?.multiple === true
  const previousNarrow = useRef(narrowKey)
  useEffect(() => {
    const before = previousNarrow.current
    previousNarrow.current = narrowKey
    if (before === narrowKey || disabled) return
    // Only a real change clears: arriving at a first answer is not one.
    if (before === undefined || before === null) return
    if (multiple ? (Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value !== '') : current !== '') {
      setLabel(undefined)
      onChange(multiple ? [] : '')
    }
  }, [narrowKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const params: Record<string, string> = {
    ...(scoped && owners ? { owners } : {}),
    ...(narrow ?? {}),
  }
  const hasParams = Object.keys(params).length > 0
  const emptyMessage = scoped && owners === null
    ? `No entries. Choose the ${ownerName} first.`
    : narrow === null ? `No entries. Choose the ${dependsOnName} first.` : undefined


  /*
    When the owner changes, the pick follows it: a contact of the supplier
    that was replaced is not a contact of this one. The first render is not a
    change — a saved document opens on what it was saved with.
  */
  const previous = useRef(owners)
  useEffect(() => {
    if (previous.current === owners) return
    const before = previous.current
    previous.current = owners
    if (disabled || !scoped || before === undefined) return
    if (owners === null) {
      if (current !== '') { setLabel(undefined); onChange('') }
      return
    }
    if (field.prefill === 'none') {
      if (current !== '') { setLabel(undefined); onChange('') }
      return
    }
    let live = true
    void api.lookupScoped(entity, owners).then((rows) => {
      if (!live) return
      const first = rows[0]
      setLabel(first?.label)
      onChange(first?.id ?? '')
    }).catch(() => undefined)
    return () => { live = false }
  }, [owners]) // eslint-disable-line react-hooks/exhaustive-deps

  // An RFQ's participants are suppliers already on its list, not records to add here.
  const addable = firstScope?.owner !== 'RFQ'
  const preset: Record<string, string> | undefined = !scoped || owners === null || !addable
    ? undefined
    : firstScope?.owner === 'OURS'
      ? { owner_type: 'COMPANY' }
      : firstScope?.field && typeof scopeValues?.[firstScope.field] === 'string'
        ? { owner_type: firstScope.owner, owner_id: String(scopeValues[firstScope.field]) }
        : undefined

  if (multiple) {
    return (
      <MultiLookup
        entity={entity}
        value={value}
        onChange={onChange}
        disabled={disabled}
        invalid={invalid}
        {...(hasParams ? { params, browse: true } : {})}
        {...(emptyMessage !== undefined ? { emptyMessage } : {})}
      />
    )
  }

  return (
    <>
      <Lookup
        entity={entity}
        value={current}
        label={label ?? valueLabel}
        disabled={disabled}
        invalid={invalid}
        {...(hasParams ? { params, browse: true } : {})}
        {...(emptyMessage !== undefined ? { emptyMessage } : {})}
        onChange={(next, option) => {
          setLabel(option?.label)
          onChange(next)
        }}
      />
      {current !== '' && CARD_LINES[entity] ? <LookupCard entity={entity} value={current} /> : null}
      {creatable && addable && !disabled && owners !== null ? (
        <div className="row" style={{ marginTop: 6, gap: 4 }}>
          <Button kind="ghost" onClick={() => setAdding(true)}>
            Add new {entity}
          </Button>
          {current !== '' ? (
            <Button kind="ghost" onClick={() => setEditing(true)}>Edit</Button>
          ) : null}
        </div>
      ) : null}

      {adding && creatable ? (
        <QuickMasterDialog
          masterKey={creatable}
          title={`New ${field.label.toLowerCase()}`}
          {...(preset ? { preset } : {})}
          onClose={() => setAdding(false)}
          onCreated={(id, created) => {
            setLabel(created)
            onChange(id)
            setAdding(false)
          }}
        />
      ) : null}

      {editing && creatable && current !== '' ? (
        <QuickMasterDialog
          masterKey={creatable}
          recordId={current}
          title={`Edit ${field.label.toLowerCase()}`}
          onClose={() => setEditing(false)}
          onCreated={(_id, saved) => {
            setLabel(saved)
            setEditing(false)
          }}
        />
      ) : null}
    </>
  )
}

/** The toolbar the reference's terms editor has (BILL-015). */
const RICH_COMMANDS: ReadonlyArray<{ command: string; label: string; text: string }> = [
  { command: 'bold', label: 'Bold', text: 'B' },
  { command: 'italic', label: 'Italic', text: 'I' },
  { command: 'underline', label: 'Underline', text: 'U' },
  { command: 'insertOrderedList', label: 'Numbered list', text: '1.' },
  { command: 'insertUnorderedList', label: 'Bulleted list', text: '•' },
  { command: 'createLink', label: 'Link', text: 'Link' },
]

/**
 * A small formatted-text editor: bold, italic, underline, numbered and
 * bulleted lists, links. What it hands back is sanitised HTML — the same
 * function the server stores and prints it through.
 */
const RichTextControl = ({ field, value, onChange, disabled }: Pick<ControlProps, 'field' | 'value' | 'onChange' | 'disabled'>) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const html = sanitizeRichText(value)

  // Written in only when it differs, so the caret is not thrown out mid-word.
  useEffect(() => {
    const el = ref.current
    if (el && el.innerHTML !== html && document.activeElement !== el) el.innerHTML = html
  }, [html])

  const emit = (): void => { if (ref.current) onChange(sanitizeRichText(ref.current.innerHTML)) }
  const run = (event: MouseEvent, command: string): void => {
    event.preventDefault()
    ref.current?.focus()
    if (command === 'createLink') {
      const url = window.prompt('Link to (https://…)')
      if (!url) return
      document.execCommand('createLink', false, url)
    } else {
      document.execCommand(command, false)
    }
    emit()
  }

  if (disabled) {
    return <div className="richtext richtext--read" dangerouslySetInnerHTML={{ __html: html }} />
  }
  return (
    <div className="richtext">
      <div className="richtext__bar" role="toolbar" aria-label={`Format ${field.label.toLowerCase()}`}>
        {RICH_COMMANDS.map((c) => (
          <button key={c.command} type="button" className="richtext__btn" aria-label={c.label} title={c.label}
            onMouseDown={(e) => run(e, c.command)}>
            {c.text}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        className="richtext__body"
        role="textbox"
        aria-multiline="true"
        aria-label={field.label}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
      />
    </div>
  )
}
